import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import parse, { HTMLReactParserOptions, Element } from 'html-react-parser';
import { imagesState, LocationState } from '../../../types/index'
import styles from './StepComponent.modules.scss';
import { AlertModal } from '../AlertModal/AlertModal';


interface StepComponentProps {
  to: string;
  category?: string;
  stepNumber: number;
  firstCollectionHandle?: string | null;
  secondCollectionHandle?: string | null;
  productHandle?: string | null;
  confirmMessage?: string;
  nayami?: string;
  content?: string;
  options?: string[];
  variants?: string[];
  shop?: string;
  factory?: string;
  email?: string;
  bland?: string;
  request?: string;
  prefecture?: string;
  area?: string;
  material?: string;
}

export const StepComponent: React.FC<StepComponentProps> = ({ to, category, stepNumber, firstCollectionHandle, secondCollectionHandle, productHandle,  content, nayami, email, bland, request, prefecture, area, material }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // HTML parser options for secure content rendering
  const parserOptions: HTMLReactParserOptions = {
    replace: (domNode) => {
      if (domNode instanceof Element) {
        const { name, attribs } = domNode;
        
        // Allow only specific safe tags
        const allowedTags = ['a', 'strong', 'b', 'em', 'i', 'u', 'span', 'p', 'br'];
        if (!allowedTags.includes(name)) {
          return <></>;
        }
        
        // For links, ensure they are safe
        if (name === 'a' && attribs?.href) {
          // Only allow https and relative links
          if (attribs.href.startsWith('https://') || attribs.href.startsWith('/')) {
            // Return undefined to let the parser handle children normally
            return undefined;
          }
          // Convert unsafe links to spans but preserve content
          return undefined;
        }
        
        // Return undefined to let parser handle other allowed tags normally
        return undefined;
      }
      return undefined;
    }
  };

  const {
    images: initialImages = {} as imagesState,
  } = (location.state as LocationState) || {};

  const handleClick = () => {
    if (to) {
      setIsModalOpen(true);
    } else {
      console.warn('リンクがありません');
    }
  };

  const handleConfirm = () => {
    setIsModalOpen(false);
    if (to) {
      // 現在のURLパラメータを取得
      const currentParams = new URLSearchParams(window.location.search);
      
      // toのURLに既存のパラメータがあるかチェック
      const [basePath, existingParams] = to.split('?');
      const urlParams = new URLSearchParams(existingParams || '');
      
      // 現在のパラメータを新しいURLパラメータにマージ（既存パラメータを優先）
      currentParams.forEach((value, key) => {
        if (!urlParams.has(key)) {
          urlParams.set(key, value);
        }
      });
      
      const finalUrl = urlParams.toString() ? `${basePath}?${urlParams.toString()}` : basePath;
      navigate(finalUrl, { state: { images: initialImages } });
    }
  };

  // suits_campaignの場合はSTEPラベルを非表示にするためのヘルパー関数
  const shouldShowStepLabel = () => {
    return firstCollectionHandle !== 'suits_campaign';
  };

  // スタイルクラスを条件付きで返す関数
  const getStepLabelClass = () => {
    return shouldShowStepLabel() ? styles.label : '';
  };

  const renderContent = () => {
    switch (stepNumber) {
      case 1:
        return (
          <div className={styles.layout}>
            <div className={styles.flex}>
              {firstCollectionHandle === 'first_other' ?
                <>
                  <div className={styles.labelFlex}>
                    <span className={getStepLabelClass()}>{shouldShowStepLabel() ? 'STEP1' : ''}</span>
                    <div>
                      <p className={styles.labelTitle}>修理したいアイテム</p>
                      <span className={styles.labelSubTitle}>その他</span>
                    </div>
                  </div>
                  <button onClick={handleClick} className={styles.changeButton}>
                    変更する
                  </button>
                </> :
                <>
                  <div className={styles.labelFlex}>
                    <span className={getStepLabelClass()}>{shouldShowStepLabel() ? 'STEP1' : ''}</span>
                    <div>
                      <p className={styles.labelTitle}>修理したいアイテム</p>
                      <span className={styles.labelSubTitle}>{category}</span>
                    </div>
                  </div>
                  <button onClick={handleClick} className={styles.changeButton}>
                    変更する
                  </button>
                </>
              }
            </div>
          </div>
        );
      case 2:
        return (
          <div className={styles.layout}>
            <div className={styles.flex}>
            {secondCollectionHandle === 'second_other' ? (
              firstCollectionHandle === 'first_other' ? (
                <>
                  <div className={styles.labelFlex}>
                    <span className={getStepLabelClass()}>{shouldShowStepLabel() ? 'STEP2' : ''}</span>
                    <div>
                      <p className={styles.labelTitle}>修理内容</p>
                      <span className={styles.labelSubTitle}>未設定</span>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className={styles.labelFlex}>
                    <span className={getStepLabelClass()}>{shouldShowStepLabel() ? 'STEP2' : ''}</span>
                    <div>
                      <p className={styles.labelTitle}>修理内容</p>
                      <span className={styles.labelSubTitle}>その他</span>
                    </div>
                  </div>
                  <button onClick={handleClick} className={styles.changeButton}>
                    変更する
                  </button>
                </>
              )
            ) : (
              <>
                <div className={styles.labelFlex}>
                  <span className={styles.label}>STEP2</span>
                  <div>
                    <p className={styles.labelTitle}>修理内容</p>
                    <span className={styles.labelSubTitle}>{category}</span>
                  </div>
                </div>
                <button onClick={handleClick} className={styles.changeButton}>
                  変更する
                </button>
              </>
            )}
            </div>
          </div>
        );
      case 3:
        return (
          <div className={styles.layout}>
            <div>
              <div className={styles.labelGrid}>
                <span className={getStepLabelClass()}>{shouldShowStepLabel() ? 'STEP3' : ''}</span>
                {productHandle === 'product_other' ? (
                  secondCollectionHandle === 'second_other' ? (
                    <>
                      <div className={styles.labelGridFlex}>
                        <div>
                          <p className={styles.labelTitle}>修理内容</p>
                          <span className={styles.labelSubTitle}>未設定</span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={styles.labelGridFlex}>
                        <p className={styles.labelTitle}>修理内容</p>
                        <button onClick={handleClick} className={styles.changeButton}>
                          変更する
                        </button>
                      </div>
                      <div className={styles.gridContent}>
                        <span className={styles.labelSubTitle}>その他</span>
                      </div>
                    </>
                  )
                ) : (
                  <>
                    <div className={styles.labelGridFlex}>
                      {/* <p className={styles.labelTitle}>{category}</p> */}
                      <p className={styles.labelTitle}>修理内容</p>
                      <button onClick={handleClick} className={styles.changeButton}>
                        変更する
                      </button>
                    </div>
                    <div className={styles.gridContent}>
                      <span className={`${styles.labelSubTitle} ${styles.weight400}`}>{category}</span>
                      <span className={`${styles.labelSubTitle} ${styles.weight400}`}>{nayami}</span>
                      <ul className={styles.description}>
                        {content && content
                          .split(/<\/?p>/)
                          .filter(line => line.trim() !== '')
                          .map((line, index) => (
                            <li key={index}>{parse(line, parserOptions)}</li>
                          ))}
                      </ul>
                      {/* <div className={styles.optionsWrapper}>
                        <IconPrice />
                        <div className={styles.options}>
                          {options.map((optionData, optionIndex) => (
                            <div key={optionIndex} className={styles.menuFlex}>
                              <p className={styles.menuName}>{optionData}</p>
                              <p className={styles.menuValue}>{variants[optionIndex]}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className={styles.shopWrapper}>
                        <IconShop />
                        <div className={styles.options}>
                          <div className={styles.menuFlex}>
                            <p className={styles.menuName}>納期(店舗)</p>
                            <p className={styles.menuValue}>{shop}</p>
                          </div>
                        </div>
                      </div>
                      <div className={styles.shopWrapper}>
                        <IconFactory />
                        <div className={styles.options}>
                          <div className={styles.menuFlex}>
                            <p className={styles.menuName}>納期(配送修理)</p>
                            <p className={styles.menuValue}>{factory}</p>
                          </div>
                        </div>
                      </div> */}
                    </div>
                  </>
                )}
            </div>
            </div>
          </div>
        );
      case 4:
        return (
          <div className={styles.layout}>
            <div>
              <div className={styles.labelGrid}>
                <span className={getStepLabelClass()}>{shouldShowStepLabel() ? 'STEP4' : ''}</span>
                <div className={styles.labelGridFlex}>
                  <p className={styles.labelTitle}>{category}</p>
                  <button onClick={handleClick} className={styles.changeButton}>
                    変更する
                  </button>
                </div>
                <div className={styles.gridContent}>
                  <p className={styles.cautionText}>【ご注意】画像が不鮮明などの理由で、追加の画像が必要な場合は後ほど画像送付をお願いする場合があります。</p>
                  <div className={styles.images}>
                    {initialImages && Object.keys(initialImages).map((key) => {
                      const imageFiles = initialImages[key];
                      if (Array.isArray(imageFiles)) {
                        return imageFiles.map((file, index) => (
                          <img key={`${key}-${index}`} src={URL.createObjectURL(file)} alt={`${key}-${index}`} width={100} height={100} />
                        ));
                      } else if (imageFiles) {
                        return <img key={key} src={URL.createObjectURL(imageFiles)} alt={key} width={100} height={100} />;
                      } else {
                        return null;
                      }
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 5:
        return (
          <div className={styles.layout}>
            <div>
              <div className={styles.labelGrid}>
                <span className={getStepLabelClass()}>{shouldShowStepLabel() ? 'STEP5' : ''}</span>
                <div className={styles.labelGridFlex}>
                  <p className={styles.labelTitle}>{category}</p>
                  <button onClick={handleClick} className={styles.changeButton}>
                    変更する
                  </button>
                </div>
                <div className={styles.gridContent}>
                  <div className={styles.wrapper}>
                    <p className={styles.labelSubTitle}>メールアドレス</p>
                    <p className={styles.text}>{email}</p>
                  </div>
                  <div className={styles.wrapper}>
                    <p className={styles.labelSubTitle}>ブランド名・メーカー名</p>
                    <p className={styles.text}>{bland}</p>
                  </div>
                  { material && (
                    <div className={styles.wrapper}>
                      <p className={styles.labelSubTitle}>素材</p>
                      <p className={styles.text}>{material}</p>
                    </div>
                    )
                  }
                  <div className={styles.wrapper}>
                    <p className={styles.labelSubTitle}>本文</p>
                    <p className={styles.text}>{request}</p>
                  </div>
                  <div className={styles.wrapper}>
                    <p className={styles.labelSubTitle}>都道府県</p>
                    <p className={styles.text}>{prefecture}</p>
                  </div>
                  <div className={styles.wrapper}>
                    <p className={styles.labelSubTitle}>お店をご利用いただく場合のエリア</p>
                    <p className={styles.text}>{area}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      {renderContent()}
      <AlertModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onConfirm={handleConfirm} />
    </div>
  );
};